import { useState, useEffect, useCallback } from 'react';
import { bookingsAPI, propertiesAPI } from '../../lib/api';
import { AdminLayout } from '../../components/AdminLayout';
import { useProperty } from '../../contexts/PropertyContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { format, addDays, subDays, isToday as isTodayFn, startOfDay } from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

function formatTimeAgo(isoStr) {
  if (!isoStr) return '—';
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  return format(new Date(isoStr), 'd MMM, HH:mm');
}

function formatOccupancy(booking) {
  const adults = booking.adults || booking.guests || 1;
  const children = booking.children || 0;
  let str = `${adults} Adult${adults !== 1 ? 's' : ''}`;
  if (children > 0) str += ` and ${children} Child${children !== 1 ? 'ren' : ''}`;
  return str;
}

function generateHousekeepingPrint(data, dateStr, propertyName) {
  const departures = data?.departures || [];
  const stayovers = data?.stayovers || [];
  const arrivals = data?.arrivals || [];

  let html = `<!DOCTYPE html><html><head><style>
    body { font-family: 'Segoe UI', Arial, sans-serif; margin: 30px 40px; color: #1a1a1a; }
    h1 { font-size: 22px; text-align: center; margin-bottom: 4px; }
    .subtitle { text-align: center; color: #666; font-size: 13px; margin-bottom: 30px; }
    h2 { font-size: 15px; margin: 28px 0 8px; border-bottom: 2px solid #1a1a1a; padding-bottom: 6px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 10px; }
    th { padding: 8px 12px; text-align: left; font-weight: 700; border-bottom: 2px solid #ccc; }
    td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; }
    .empty { color: #999; font-style: italic; padding: 12px 0; }
    .signoff { margin-top: 60px; border-top: 1px solid #ccc; padding-top: 20px; }
    .signoff-grid { display: flex; justify-content: space-between; margin-top: 40px; }
    .signoff-box { width: 45%; }
    .signoff-line { border-bottom: 1px solid #333; height: 30px; margin-bottom: 6px; }
    .signoff-label { font-size: 11px; color: #666; }
    @media print { body { margin: 15px 20px; } }
  </style></head><body>`;

  html += `<h1>Housekeeping Report</h1>`;
  html += `<p class="subtitle">${propertyName ? propertyName + ' — ' : ''}${dateStr} — Printed ${format(new Date(), 'HH:mm')}</p>`;

  // Departing
  html += `<h2>Departing (${departures.length})</h2>`;
  if (departures.length > 0) {
    html += `<table><tr><th>Bookable Unit</th><th>Room #</th><th>Checked Out</th><th>Occupancy</th><th>Cleaned</th></tr>`;
    departures.forEach(b => {
      html += `<tr><td>${b.room_type_name || '—'}</td><td>${b.room_number || '—'}</td><td>${b.checked_out ? 'Yes' : 'No'}</td><td>${formatOccupancy(b)}</td><td>${b.room_cleaned ? 'Yes' : '___'}</td></tr>`;
    });
    html += `</table>`;
  } else { html += `<p class="empty">No departures</p>`; }

  // Stayovers
  html += `<h2>Stayovers (${stayovers.length})</h2>`;
  if (stayovers.length > 0) {
    html += `<table><tr><th>Bookable Unit</th><th>Room #</th><th>Checked Out</th><th>Occupancy</th></tr>`;
    stayovers.forEach(b => {
      html += `<tr><td>${b.room_type_name || '—'}</td><td>${b.room_number || '—'}</td><td>No</td><td>${formatOccupancy(b)}</td></tr>`;
    });
    html += `</table>`;
  } else { html += `<p class="empty">No stayovers</p>`; }

  // Arriving
  html += `<h2>Arriving (${arrivals.length})</h2>`;
  if (arrivals.length > 0) {
    html += `<table><tr><th>Bookable Unit</th><th>Room #</th><th>Checked In</th><th>Occupancy</th></tr>`;
    arrivals.forEach(b => {
      html += `<tr><td>${b.room_type_name || '—'}</td><td>${b.room_number || '—'}</td><td>${b.checked_in ? 'Yes' : 'No'}</td><td>${formatOccupancy(b)}</td></tr>`;
    });
    html += `</table>`;
  } else { html += `<p class="empty">No arrivals</p>`; }

  // Sign-off
  html += `<div class="signoff">`;
  html += `<p style="font-size:13px;font-weight:600;">Sign-Off</p>`;
  html += `<div class="signoff-grid">`;
  html += `<div class="signoff-box"><div class="signoff-line"></div><div class="signoff-label">Housekeeper Name & Signature</div></div>`;
  html += `<div class="signoff-box"><div class="signoff-line"></div><div class="signoff-label">Manager Name & Signature</div></div>`;
  html += `</div>`;
  html += `<div style="margin-top:24px"><div class="signoff-line" style="width:30%"></div><div class="signoff-label">Date</div></div>`;
  html += `</div>`;

  html += `</body></html>`;

  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 500);
}

export default function AdminHousekeeping() {
  return (
    <AdminLayout>
      <HousekeepingContent />
    </AdminLayout>
  );
}

function HousekeepingContent() {
  const { selectedProperty } = useProperty();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [togglingId, setTogglingId] = useState(null);

  const fetchData = useCallback(async (date) => {
    setLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const [dailyRes, propsRes] = await Promise.all([
        bookingsAPI.daily(dateStr, selectedProperty || undefined),
        propertiesAPI.list(),
      ]);
      setData(dailyRes.data);
      setProperties(propsRes.data || []);
    } catch {
      toast.error('Failed to load housekeeping data');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty]);

  useEffect(() => { fetchData(selectedDate); }, [selectedDate, fetchData]);

  const handleToggleCleaned = async (bookingId) => {
    setTogglingId(bookingId);
    try {
      await bookingsAPI.toggleCleaned(bookingId);
      // Update local state
      setData(prev => {
        if (!prev) return prev;
        const update = (list) => list.map(b => b.id === bookingId ? { ...b, room_cleaned: !b.room_cleaned } : b);
        return { ...prev, departures: update(prev.departures), arrivals: update(prev.arrivals), stayovers: update(prev.stayovers) };
      });
    } catch {
      toast.error('Failed to update cleaned status');
    } finally {
      setTogglingId(null);
    }
  };

  // Date strip: show 9 days centered on selected date
  const dateStrip = [];
  for (let i = -4; i <= 4; i++) {
    dateStrip.push(addDays(startOfDay(selectedDate), i));
  }

  const goToday = () => setSelectedDate(new Date());
  const goPrev = () => setSelectedDate(d => subDays(d, 1));
  const goNext = () => setSelectedDate(d => addDays(d, 1));

  const departures = data?.departures || [];
  const stayovers = data?.stayovers || [];
  const arrivals = data?.arrivals || [];

  const selectedPropName = selectedProperty
    ? properties.find(p => p.id === selectedProperty)?.name || ''
    : 'All Properties';

  const dateDisplay = format(selectedDate, 'EEEE, d MMM yyyy');

  return (
    <div className="page-wrapper" data-testid="housekeeping-page">
      {/* Breadcrumb + Title + PDF */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title" data-testid="housekeeping-title">
            Housekeeping
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Room cleaning status and daily turnover.</p>
        </div>
        <Button
          variant="outline"
          onClick={() => generateHousekeepingPrint(data, dateDisplay, selectedPropName)}
          className="gap-2"
          data-testid="housekeeping-print-btn"
        >
          <FileText className="h-4 w-4" />
          PDF {format(selectedDate, 'EEE, d MMM').toUpperCase()}
        </Button>
      </div>

      {/* Date Navigation Strip */}
      <div className="flex items-center gap-1" data-testid="date-strip">
        <button onClick={goPrev} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0" data-testid="hk-prev-day">
          <ChevronLeft className="h-5 w-5 text-gray-500" />
        </button>
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide flex-1 justify-center">
          {dateStrip.map((d) => {
            const isSelected = format(d, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
            const isT = isTodayFn(d);
            return (
              <button
                key={d.toISOString()}
                onClick={() => setSelectedDate(d)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                  isSelected
                    ? isT ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-gray-900 text-white'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
                data-testid={`date-${format(d, 'yyyy-MM-dd')}`}
              >
                {isT && isSelected ? 'TODAY' : format(d, 'EEE, d MMM').toUpperCase()}
              </button>
            );
          })}
        </div>
        <button onClick={goNext} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0" data-testid="hk-next-day">
          <ChevronRight className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Departing */}
          <section data-testid="section-departing">
            <h2 className="text-sm font-bold tracking-wide uppercase text-gray-900 mb-1 flex items-center gap-2">
              Departing
              <Badge variant="outline" className="text-xs">{departures.length}</Badge>
            </h2>
            <div className="h-0.5 bg-gray-900 mb-3" />
            {departures.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No departures for this date.</p>
            ) : (
              <div className="bg-white rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bookable Unit</TableHead>
                      <TableHead>Room #</TableHead>
                      <TableHead>Checked Out</TableHead>
                      <TableHead>Occupancy</TableHead>
                      <TableHead className="text-center">Cleaned?</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {departures.map(b => (
                      <TableRow key={b.id} data-testid={`dep-row-${b.id}`}>
                        <TableCell className="font-medium">{b.room_type_name || '—'}</TableCell>
                        <TableCell>{b.room_number || '—'}</TableCell>
                        <TableCell>
                          {b.checked_out
                            ? <span className="text-emerald-600">{formatTimeAgo(b.checked_out_at)}</span>
                            : <span className="text-muted-foreground">No</span>
                          }
                        </TableCell>
                        <TableCell>{formatOccupancy(b)}</TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={!!b.room_cleaned}
                            onCheckedChange={() => handleToggleCleaned(b.id)}
                            disabled={togglingId === b.id}
                            data-testid={`cleaned-toggle-${b.id}`}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>

          {/* Stayovers */}
          <section data-testid="section-stayovers">
            <h2 className="text-sm font-bold tracking-wide uppercase text-gray-900 mb-1 flex items-center gap-2">
              Stayovers
              <Badge variant="outline" className="text-xs">{stayovers.length}</Badge>
            </h2>
            <div className="h-0.5 bg-gray-900 mb-3" />
            {stayovers.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No stayovers for this date.</p>
            ) : (
              <div className="bg-white rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bookable Unit</TableHead>
                      <TableHead>Room #</TableHead>
                      <TableHead>Checked Out</TableHead>
                      <TableHead>Occupancy</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stayovers.map(b => (
                      <TableRow key={b.id} data-testid={`stay-row-${b.id}`}>
                        <TableCell className="font-medium">{b.room_type_name || '—'}</TableCell>
                        <TableCell>{b.room_number || '—'}</TableCell>
                        <TableCell><span className="text-muted-foreground">No</span></TableCell>
                        <TableCell>{formatOccupancy(b)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>

          {/* Arriving */}
          <section data-testid="section-arriving">
            <h2 className="text-sm font-bold tracking-wide uppercase text-gray-900 mb-1 flex items-center gap-2">
              Arriving
              <Badge variant="outline" className="text-xs">{arrivals.length}</Badge>
            </h2>
            <div className="h-0.5 bg-gray-900 mb-3" />
            {arrivals.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No arrivals for this date.</p>
            ) : (
              <div className="bg-white rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bookable Unit</TableHead>
                      <TableHead>Room #</TableHead>
                      <TableHead>Checked In</TableHead>
                      <TableHead>Occupancy</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {arrivals.map(b => (
                      <TableRow key={b.id} data-testid={`arr-row-${b.id}`}>
                        <TableCell className="font-medium">{b.room_type_name || '—'}</TableCell>
                        <TableCell>{b.room_number || '—'}</TableCell>
                        <TableCell>
                          {b.checked_in
                            ? <span className="text-emerald-600">{formatTimeAgo(b.checked_in_at)}</span>
                            : <span className="text-muted-foreground">No</span>
                          }
                        </TableCell>
                        <TableCell>{formatOccupancy(b)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
